import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Card, Col, Image, Row, Typography } from 'antd';
import React, { useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import TranslateText from 'components/hoc/TranslateText';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import usePageName from 'hooks/usePageName';
import useProfily from 'hooks/useProfily';

import emitentApi from 'services/emitent';
import nastavitUcetApi from 'services/nastavitUcet';
import { crsHistorieGet } from 'services/poDlt/crs';
import { IPristupy, ISeznamNotifikaci } from 'services/profily';

import { EEmitentJe } from 'enums/isin/emitentJe';
import { EProfilyTyp } from 'enums/profilyTyp';
import routePathEnum from 'enums/routePathEnum';

import companyIcon from 'styles/images/companyIcon.svg';
import next from 'styles/images/next.svg';

const titleStyle = {
    marginTop: 8,
    marginBottom: 4,
    fontWeight: 700,
    fontSize: 20
};

interface IDashboardCard {
    profily: IPristupy[];
    isDetailUctu?: boolean;
    disabled?: boolean;
}

interface ISortedProfiles {
    EMITENT?: IPristupy[];
    LEI?: IPristupy[];
    STATNISPRAVA?: IPristupy[];
    AKCIONAR?: IPristupy[];
    CDCPSRD?: IPristupy[];
    ADMEMIT?: IPristupy[];
    UCASTNIK?: IPristupy[];
    SPRAVCEUCTU?: IPristupy[];
    SPRAVCELEI?: IPristupy[];
    ADMINISTRATOR?: IPristupy[];
    TEXTY?: IPristupy[];
    ADMINVFO?: IPristupy[];
    PODLT?: IPristupy[];
    JINAOSOBA?: IPristupy[];
}

interface ISortedProfilesTexts {
    EMITENT: string[];
    LEI: string[];
    STATNISPRAVA: string[];
    AKCIONAR: string[];
    CDCPSRD: string[];
    ADMEMIT: string[];
    UCASTNIK: string[];
    SPRAVCEUCTU: string[];
    SPRAVCELEI: string[];
    ADMINISTRATOR: string[];
    TEXTY: string[];
    ADMINVFO: string[];
    PODLT: string[];
    JINAOSOBA: string[];
}

const sortedProfilestexts: ISortedProfilesTexts = {
    PODLT: [],
    EMITENT: [
        'AMLZ',
        'SLUZBY_ISIN',
        'SS_MENU_TITLE',
        'DASHBOARD_CARD_REGISTER_VYPIS',
        'INFORMACNI_SLUZBY',
        'KORPORATNI_SRD',
        'DASHBOARD_CARD_SPRAVA_EMITENTA'
    ],
    LEI: [],
    STATNISPRAVA: ['DASHBOARD_CARD_INFORMACE_UPLATU', 'DASHBOARD_CARD_UCTY_VLASTNIKU', 'DASHBOARD_CARD_EMISE_CP'],
    AKCIONAR: [
        'DASHBOARD_CARD_MAJETKOVE_UCTY',
        'PREHLED_UDALOSTI',
        'DASHBOARD_CARD_SLUZBY_ACKIONAR',
        'DASHBOARD_CARD_SLUZBYVH',
        'DASHBOARD_CARD_HLASOVANI'
    ],
    CDCPSRD: [],
    ADMEMIT: ['SS_MENU_TITLE', 'DASHBOARD_CARD_REGISTER_VYPIS', 'INFORMACNI_SLUZBY', 'KORPORATNI_SRD', 'SPRAVA'],
    UCASTNIK: ['DASHBOARD_CARD_PREHLED_MU', 'DASHBOARD_CARD_SLUZBYVH', 'DASHBOARD_CARD_HLASOVANI'],
    SPRAVCEUCTU: ['DASHBOARD_CARD_PREHLED_MU', 'DASHBOARD_CARD_SLUZBYVH', 'DASHBOARD_CARD_HLASOVANI'],
    SPRAVCELEI: [],
    ADMINISTRATOR: [],
    TEXTY: ['SPRAVCE_TEXTY_TEXT'],
    ADMINVFO: ['VYPIS_REGISTER'],
    JINAOSOBA: ['TEXT']
};

const cardLiStyle = {
    fontSize: 17
};

// Pole jednotlivych moznosti pro notifikace na karte
const notifikaceTypMapa = [
    {
        typ: 'ZELENE',
        class: 'dash-card-not-green',
        icon: <InfoCircleOutlined className="dash-card-not-green-icon" />
    },
    {
        typ: 'ZLUTE',
        class: 'dash-card-not-yellow',
        icon: <WarningOutlined className="dash-card-not-yellow-icon" />
    },
    {
        typ: 'CERVENE',
        class: 'dash-card-not-red',
        icon: <WarningOutlined className="dash-card-not-red-icon" />
    }
];

export const notifikaceMap = (notifikace: ISeznamNotifikaci, i: number) => {
    return notifikaceTypMapa.map((x, index) => {
        if (x.typ === notifikace.typ) {
            return (
                <div className={x.class} key={index}>
                    <Typography.Title level={4} key={i}>
                        {x.icon}
                        {notifikace.text}
                    </Typography.Title>
                </div>
            );
        }
    });
};
// DRY
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const title = (text?: any) => (
    <Row
        style={{
            flexFlow: 'nowrap'
        }}
    >
        <Col
            style={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 40
            }}
        >
            <Image alt="company icon" src={companyIcon} preview={false} className="dash-card-add-icon" />
        </Col>
        <Col
            style={{
                display: 'flex',
                alignItems: 'center',
                flexFlow: 'wrap',
                flexGrow: 1
            }}
        >
            <TranslateText>{text ? text : ''}</TranslateText>
        </Col>
    </Row>
);

export const getAvailablePages = (profil: IPristupy) => {
    // 'AMLZ'
    // 'ISIN'
    // 'SPRAVA uživatelů' WRSPRAVA
    // 'Elektronické služby (úpisy)' PRISTUPY_ELSLUZBY
    // 'SS_MENU_TITLE (výpisy)' PRISTUPY_EMISNIOP
    // 'SRD (korporátní akce)' PRISTUPY_SRDKORPAKCE

    return (
        <ul
            style={{
                position: 'relative',
                left: -20
            }}
        >
            {profil.typ !== EProfilyTyp.ADMEMIT &&
                profil.typ !== EProfilyTyp.CDCPSRD &&
                profil.typ !== EProfilyTyp.STATNISPRAVA &&
                profil.typ !== EProfilyTyp.AKCIONAR &&
                profil.typ !== EProfilyTyp.SPRAVCEUCTU &&
                profil.typ !== EProfilyTyp.UCASTNIK &&
                profil.typ !== EProfilyTyp.ADMINVFO &&
                profil.typ !== EProfilyTyp.SPRAVCELEI &&
                profil.typ !== EProfilyTyp.PODLT && (
                    <>
                        <li
                            style={{
                                // fontVariant: 'small-caps',
                                fontSize: 17,
                                fontWeight: 400
                            }}
                        >
                            AMLZ
                        </li>
                        <li
                            style={{
                                // fontVariant: 'small-caps',
                                fontSize: 17,
                                fontWeight: 400
                            }}
                        >
                            <TranslateText>SLUZBY_ISIN</TranslateText>
                        </li>
                    </>
                )}
            {profil.emitentprava?.adminwr && profil.emitentprava.adminwr && (
                <li
                    style={{
                        // fontVariant: 'small-caps',
                        fontSize: 17,
                        fontWeight: 400
                    }}
                >
                    <TranslateText>WRSPRAVA</TranslateText>
                </li>
            )}
            {profil.emitentprava?.vypis && profil.emitentprava.vypis && (
                <li
                    style={{
                        // fontVariant: 'small-caps',
                        fontSize: 17,
                        fontWeight: 400
                    }}
                >
                    <TranslateText>VYPIS_REGISTER</TranslateText>
                </li>
            )}
            {((profil.emitentprava?.upis && profil.emitentprava.upis) || profil.emitentprava?.upissicav) && (
                <li
                    style={{
                        // fontVariant: 'small-caps',
                        fontSize: 17,
                        fontWeight: 400
                    }}
                >
                    <TranslateText>SS_MENU_TITLE</TranslateText>
                </li>
            )}
            {profil.emitentprava?.srd && profil.emitentprava.srd && (
                <li
                    style={{
                        // fontVariant: 'small-caps',
                        fontSize: 17,
                        fontWeight: 400
                    }}
                >
                    <TranslateText>PRISTUPY_SRDKORPAKCE</TranslateText>
                </li>
            )}
            {profil.typ === EProfilyTyp.SPRAVCELEI && (
                <li
                    style={{
                        // fontVariant: 'small-caps',
                        fontSize: 17,
                        fontWeight: 400
                    }}
                >
                    LEI
                </li>
            )}
        </ul>
    );
};

export const extraButton = (index: number | string) => {
    return (
        <ButtonComponent
            id={`dashBoardCardButton${index}`}
            icon={<Image alt="next icon" src={next} preview={false} height="20px" width="10px" />}
            buttontext=""
            type="text"
            style={{
                marginTop: '0.9em'
            }}
        />
    );
};

const DashboardCard: React.FC<IDashboardCard> = ({ profily, isDetailUctu, disabled }) => {
    const { setLevel } = useProfily();
    const { navigateWorkaround } = useNavigateWorkaround();
    const [sortedProfiles, setSortedProfiles] = useState<ISortedProfiles>({});
    const detailUctuTyp = usePageName();

    useEffect(() => {
        if (profily && profily.length) {
            const profilyObj: ISortedProfiles = {};

            profily.map(e => {
                profilyObj[e.typ] ? profilyObj[e.typ]!.push(e) : ((profilyObj[e.typ] = []), profilyObj[e.typ]!.push(e));
            });

            setSortedProfiles(profilyObj);
        }
    }, [profily]);

    const handleClick = (ucet: IPristupy) => {
        // TODO dat do jednej funkcie
        setLevel('dashboardLoading', true);
        setLevel('amlz', null);
        setLevel('lei', null);
        setLevel('menuProfile', null);
        setLevel('dashboardWrapperTopSelected', null);
        setLevel('aktivniFirma', ucet);
        nastavitUcetApi(ucet?.iducet).then(() => {
            if (
                ucet.typ === EProfilyTyp.EMITENT ||
                ucet.typ === EProfilyTyp.CDCPSRD ||
                ucet.typ === EProfilyTyp.ADMEMIT
            ) {
                return emitentApi().then(data => {
                    if (Object.keys(data).length) {
                        setLevel('menuProfile', {
                            ...data,
                            typemitenta: data.typemitenta || EEmitentJe.NA
                        });
                        data.vypis &&
                            (setLevel('dashboardLoading', false),
                            navigateWorkaround(
                                ucet.typ === 'AKCIONAR'
                                    ? `../firma/${ucet.iducet}${routePathEnum.PREHLED_AKCIONAR}`
                                    : `../firma/${ucet.iducet}${routePathEnum.PREHLED}`
                            ));
                        !data.vypis &&
                            (navigateWorkaround(
                                ucet.typ === 'AKCIONAR'
                                    ? `../firma/${ucet.iducet}${routePathEnum.PREHLED_AKCIONAR}`
                                    : `../firma/${ucet.iducet}${routePathEnum.PREHLED}`
                            ),
                            setLevel('dashboardLoading', false));
                    } else {
                        navigateWorkaround(
                            ucet.typ === 'AKCIONAR'
                                ? `../firma/${ucet.iducet}${routePathEnum.PREHLED_AKCIONAR}`
                                : `../firma/${ucet.iducet}${routePathEnum.PREHLED}`
                        );
                        setLevel('dashboardLoading', false);
                    }
                });
            } else if ((ucet.typ === EProfilyTyp.STATNISPRAVA && ucet.useroid) || ucet.typ === EProfilyTyp.ADMINVFO) {
                return (
                    setLevel('dashboardLoading', false),
                    navigateWorkaround(`/firma/${ucet.iducet}${routePathEnum.ME_POZADAVKY}`)
                );
            } else if (ucet.typ === EProfilyTyp.PODLT) {
                return emitentApi().then(data => {
                    const redirect = (crsHotovo: boolean) => {
                        setLevel('menuProfile', {
                            ...data,
                            crsHotovo: crsHotovo,
                            typemitenta: data.typemitenta || EEmitentJe.NA
                        });
                        setLevel('dashboardLoading', false);
                        navigateWorkaround(`/firma/${ucet.iducet}${routePathEnum.PREHLED_PRAVNICKA}`);
                    };
                    crsHistorieGet({
                        order: 'id:desc',
                        limit: 1,
                        offset: 0
                    })
                        .then(resp => {
                            redirect(!!resp.lastid);
                        })
                        .catch(() => {
                            redirect(false);
                        });
                });
            } else if (ucet.typ === EProfilyTyp.SPRAVCELEI) {
                navigateWorkaround(`/firma/${ucet.iducet}${routePathEnum.LEI}`);
                setLevel('dashboardLoading', false);
            } else if (ucet.typ === EProfilyTyp.TEXTY) {
                navigateWorkaround(`/firma/${ucet.iducet}${routePathEnum.APLIKACNI_TEXTY}`);
                setLevel('dashboardLoading', false);
            } else {
                setLevel('dashboardLoading', false);
                navigateWorkaround(
                    ucet.typ === 'AKCIONAR'
                        ? `/firma/${ucet.iducet}${routePathEnum.PREHLED_AKCIONAR}`
                        : `/firma/${ucet.iducet}${routePathEnum.PREHLED}`
                );
            }
        });
    };
    // Pro kazdy typ se vypisuje jina karta, proto podminky zobrazeni na pristupy.typ
    return (
        <Row gutter={16} justify="start">
            {!isDetailUctu && (
                <>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12} className="dashboard-card-col-card">
                        <AnchorComponent
                            onClick={() =>
                                sortedProfiles.SPRAVCELEI?.length
                                    ? handleClick(sortedProfiles.SPRAVCELEI[0])
                                    : navigateWorkaround(routePathEnum.NOVY_UCET.replace(':typ?', 'SPRAVCELEI'))
                            }
                            className={disabled ? 'hover-border-disabled' : 'hover-border'}
                        >
                            <Card
                                title={
                                    <h4 style={titleStyle}>
                                        <TranslateText>SLUZBY_LEI</TranslateText>
                                    </h4>
                                }
                                className="dash-card"
                                style={{
                                    height: '100%'
                                }}
                                extra={extraButton('LEI')}
                            >
                                <li style={cardLiStyle}>
                                    <TranslateText>LEI_MENU_NOVY</TranslateText>
                                </li>
                                <li style={cardLiStyle}>
                                    <TranslateText>MENU_LEI_SPRAVA</TranslateText>
                                </li>
                                <li style={cardLiStyle}>
                                    <TranslateText>LEI_SPRAVOVANE</TranslateText>
                                </li>
                            </Card>
                        </AnchorComponent>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12} className="dashboard-card-col-card">
                        <AnchorComponent
                            onClick={
                                disabled
                                    ? () => {}
                                    : () =>
                                          sortedProfiles.EMITENT?.length
                                              ? navigateWorkaround(routePathEnum.PROFIL_EMITENT_PREHLED)
                                              : navigateWorkaround(routePathEnum.NOVY_UCET.replace(':typ?', 'EMITENT'))
                            }
                            className={disabled ? 'hover-border-disabled' : 'hover-border'}
                        >
                            <Card
                                title={
                                    <h4 style={titleStyle}>
                                        <TranslateText>SLUZBY_EMITENT</TranslateText>
                                    </h4>
                                }
                                extra={extraButton('EMITENT')}
                                style={{
                                    height: '100%'
                                }}
                                className="dash-card"
                            >
                                {sortedProfilestexts?.EMITENT.map((u, idxLi) => (
                                    <li key={idxLi}>
                                        <TranslateText>{u}</TranslateText>
                                    </li>
                                ))}
                            </Card>
                        </AnchorComponent>
                    </Col>
                </>
            )}
            {Object.keys(sortedProfiles || {})
                .filter(
                    e =>
                        e !== EProfilyTyp.SPRAVCELEI &&
                        e !== EProfilyTyp.EMITENT &&
                        (isDetailUctu ? e === detailUctuTyp : true)
                )
                .map((e, i) =>
                    !isDetailUctu ? (
                        <Col key={i} xs={24} sm={24} md={12} lg={12} xl={12} className="dashboard-card-col-card">
                            <AnchorComponent
                                onClick={() => {
                                    if (e === 'TEXTY') {
                                        const textyProfil = profily?.filter(u => u.typ === EProfilyTyp.TEXTY)[0];
                                        setLevel('aktivniFirma', textyProfil);
                                        navigateWorkaround(
                                            `/firma/${textyProfil.iducet}${routePathEnum.APLIKACNI_TEXTY}`
                                        );
                                    } else {
                                        navigateWorkaround(routePathEnum.DETAIL_UCTU.replace(':typ?', e));
                                    }
                                }}
                                className={disabled ? 'hover-border-disabled' : 'hover-border'}
                            >
                                <Card
                                    title={
                                        <h4 style={titleStyle}>
                                            <TranslateText>{`DASHBOARD_CARD_UCTY_${e}`}</TranslateText>
                                        </h4>
                                    }
                                    extra={extraButton(i)}
                                    style={{
                                        height: '100%'
                                    }}
                                    className="dash-card"
                                >
                                    {sortedProfilestexts[e as keyof ISortedProfilesTexts]?.map((u, idxLi) => (
                                        <li key={idxLi}>
                                            <TranslateText>{u}</TranslateText>
                                        </li>
                                    ))}
                                </Card>
                            </AnchorComponent>
                        </Col>
                    ) : (
                        sortedProfiles[e as EProfilyTyp]?.map((pristupy, index) => (
                            <Col
                                xs={24}
                                sm={24}
                                md={12}
                                lg={12}
                                xl={12}
                                key={index}
                                className="dashboard-card-col-card"
                            >
                                <AnchorComponent
                                    onClick={() => handleClick(pristupy)}
                                    className={disabled ? 'hover-border-disabled' : 'hover-border'}
                                >
                                    {pristupy.typ === EProfilyTyp.LEI && (
                                        <Card
                                            key={`${index}${pristupy.spolecnost}`}
                                            title={title(
                                                <h4 style={titleStyle}>
                                                    <TranslateText>{pristupy.typ}</TranslateText>
                                                </h4>
                                            )}
                                            className="dash-card"
                                            extra={extraButton(i)}
                                            style={{
                                                height: '100%'
                                            }}
                                        >
                                            {pristupy.seznotifikaci?.map(notifikaceMap)}
                                            {getAvailablePages(pristupy)}
                                        </Card>
                                    )}

                                    {pristupy.typ === EProfilyTyp.AKCIONAR && (
                                        <Card
                                            key={`${index}${pristupy.spolecnost}`}
                                            className="dash-card"
                                            title={
                                                <h4 style={titleStyle}>
                                                    <TranslateText>CISLO_UCTU</TranslateText> {pristupy.spolecnost}
                                                </h4>
                                            }
                                            extra={extraButton(i)}
                                            style={{
                                                height: '100%'
                                            }}
                                        >
                                            <p>
                                                <TranslateText>EMAIL</TranslateText>: {pristupy.email}
                                            </p>
                                            <p>
                                                <TranslateText>TELEFON</TranslateText>: {pristupy.telefon}
                                            </p>
                                            {pristupy.seznotifikaci?.map(notifikaceMap)}
                                            {getAvailablePages(pristupy)}
                                        </Card>
                                    )}

                                    {(pristupy.typ === EProfilyTyp.STATNISPRAVA ||
                                        pristupy.typ === EProfilyTyp.ADMEMIT ||
                                        pristupy.typ === EProfilyTyp.CDCPSRD) && (
                                        <Card
                                            key={`${index}${pristupy.spolecnost}`}
                                            title={title(
                                                <h4 style={titleStyle}>
                                                    {pristupy?.profil === EProfilyTyp.STATNISPRAVA ? (
                                                        (() => {
                                                            return (
                                                                <TranslateText>
                                                                    {'PROFIL_' +
                                                                        (pristupy?.profil
                                                                            ? pristupy?.profil.includes('A')
                                                                                ? pristupy?.profil.slice(0, -1)
                                                                                : pristupy?.profil
                                                                            : '')}
                                                                </TranslateText>
                                                            );
                                                        })()
                                                    ) : (
                                                        <TranslateText>
                                                            {'DASHBOARD_CARD_PROFIL_' +
                                                                (pristupy?.profil
                                                                    ? pristupy?.profil.includes('A')
                                                                        ? pristupy?.profil.slice(0, -1)
                                                                        : pristupy?.profil
                                                                    : pristupy.typ)}
                                                        </TranslateText>
                                                    )}
                                                </h4>
                                            )}
                                            className="dash-card"
                                            style={{
                                                height: '100%'
                                            }}
                                            extra={extraButton(i)}
                                        >
                                            <p>{pristupy.spolecnost}</p>
                                            <p>
                                                <TranslateText>FORMULARE_IC</TranslateText>: {pristupy.ic}
                                            </p>
                                            {pristupy.seznotifikaci?.map(notifikaceMap)}
                                            {getAvailablePages(pristupy)}
                                        </Card>
                                    )}
                                    {pristupy.typ === EProfilyTyp.ADMINVFO && (
                                        <Card
                                            key={`${index}${pristupy.spolecnost}`}
                                            title={title(
                                                <h4 style={titleStyle}>
                                                    {
                                                        <TranslateText>
                                                            {'DASHBOARD_CARD_PROFIL_' + pristupy.typ}
                                                        </TranslateText>
                                                    }
                                                </h4>
                                            )}
                                            className="dash-card"
                                            style={{
                                                height: '100%'
                                            }}
                                            extra={extraButton(i)}
                                        >
                                            <p>{pristupy.spolecnost}</p>
                                            {pristupy.seznotifikaci?.map(notifikaceMap)}
                                            {getAvailablePages(pristupy)}
                                        </Card>
                                    )}
                                    {pristupy.typ === EProfilyTyp.UCASTNIK && (
                                        <Card
                                            key={`${index}${pristupy.spolecnost}`}
                                            title={title(
                                                <h4 style={titleStyle}>
                                                    {
                                                        <TranslateText>
                                                            {'DASHBOARD_CARD_PROFIL_' + pristupy.typ}
                                                        </TranslateText>
                                                    }
                                                </h4>
                                            )}
                                            className="dash-card"
                                            style={{
                                                height: '100%'
                                            }}
                                            extra={extraButton(i)}
                                        >
                                            <p>{pristupy.spolecnost}</p>
                                            <p>
                                                <TranslateText>UCASTNIK</TranslateText> {pristupy.ic}
                                            </p>
                                            {pristupy.seznotifikaci?.map(notifikaceMap)}
                                            {getAvailablePages(pristupy)}
                                        </Card>
                                    )}
                                    {(pristupy.typ === EProfilyTyp.SPRAVCEUCTU ||
                                        pristupy.typ === EProfilyTyp.PODLT) && (
                                        <Card
                                            key={`${index}${pristupy.spolecnost}`}
                                            title={title(
                                                <h4 style={titleStyle}>
                                                    {
                                                        <TranslateText>
                                                            {pristupy.typ === EProfilyTyp.PODLT
                                                                ? pristupy.spolecnost || ''
                                                                : 'DASHBOARD_CARD_PROFIL_' + pristupy.typ}
                                                        </TranslateText>
                                                    }
                                                </h4>
                                            )}
                                            className="dash-card"
                                            style={{
                                                height: '100%'
                                            }}
                                            extra={extraButton(i)}
                                        >
                                            {pristupy.typ !== EProfilyTyp.PODLT && <p>{pristupy.spolecnost}</p>}
                                            <p>
                                                <TranslateText>FORMULARE_IC</TranslateText> {pristupy.ic}
                                            </p>
                                            {pristupy.typ === EProfilyTyp.PODLT && <p>{pristupy.adresa}</p>}
                                            {pristupy.seznotifikaci?.map(notifikaceMap)}
                                            {getAvailablePages(pristupy)}
                                        </Card>
                                    )}
                                    {pristupy.typ === EProfilyTyp.TEXTY && (
                                        <Card
                                            title={title(
                                                <h4 style={titleStyle}>
                                                    {<TranslateText>DASHBOARD_CARD_UCTY_TEXTY</TranslateText>}
                                                </h4>
                                            )}
                                            className="dash-card"
                                            style={{
                                                height: '100%'
                                            }}
                                            extra={extraButton(i)}
                                        >
                                            <p>
                                                <TranslateText>SPRAVCE_TEXTY_TEXT</TranslateText>
                                            </p>
                                        </Card>
                                    )}
                                </AnchorComponent>
                            </Col>
                        ))
                    )
                )}
        </Row>
    );
};

export default DashboardCard;
